/**
 * The first-run walkthrough.
 *
 * Somebody who has just signed up lands on an account with nothing in it. Every figure is zero,
 * every list is empty, and the rail offers twenty screens without saying which of them has to
 * happen first. This is the answer to that: an ordered route from an empty account to a working
 * register, with each step marked done from the **records that actually exist**.
 *
 * Three rules hold the whole thing together.
 *
 * **1. Progress is observed, never asserted.** There is no tick to press. `/onboarding/progress`
 * counts what is in the books and a step is done when its record exists. A checklist somebody
 * ticks themselves drifts from the truth within a day: it congratulates an agency that has
 * nothing, and it nags one that did the work in a different order — and being nagged about a job
 * you have already finished is the fastest way to teach somebody that a panel is decorative.
 *
 * **2. It is module-aware at every level.** An organisation holding only letting is never shown a
 * plot or a property, and one holding only development is never shown a tenancy. That is not
 * tidiness: a developer who never lets anything, told to "create a tenancy", has been handed a
 * screen the API will refuse them with a 402. The endpoint omits the counts, `buildRoute` omits
 * the section, and both halves have to fail for a step to appear wrongly. ADR-0080.
 *
 * **3. A step nobody may perform is not shown.** The application's standing rule is that what a
 * role cannot do is simply absent — not greyed out, not refused on click. An accountant has no
 * business being told to add a property, so the route is narrowed by `can()` and the count of
 * steps narrows with it.
 *
 * **Where the dismissal is kept, and why it is not on the server.** `localStorage`, keyed by
 * organisation. There is no column for a per-person preference on `orgs`, `users` or
 * `memberships`, and adding one to hide a panel would be a migration for a boolean — so this is
 * deliberately per-browser. The consequence is real and worth knowing: dismissing the guide on a
 * laptop does not dismiss it on a phone. It is recorded in the session log; if it ever needs to
 * follow the person, the honest fix is a preferences column and not a longer key.
 */

import { useCallback, useMemo, useSyncExternalStore, type ComponentType } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MODULE_INFO, type ModuleCode } from "@rentorng/core";
import type { Permission } from "@rentorng/core/permissions";
import type { Vocabulary } from "@rentorng/core";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { useActiveModule, useModuleLink } from "@/lib/module-rail";
import { Button, Card, LinkButton, Spinner } from "@/components/ui";
import { InfoTip } from "@/components/info-tip";
import { MeterBar } from "@/components/chart";
import {
  IconBank,
  IconBuilding,
  IconCheck,
  IconContract,
  IconDoor,
  IconInvoice,
  IconSettings,
  IconUsers,
} from "@/components/icons";
import { IconArrowRight, IconEstate, IconFlag, IconKeys, IconPlot } from "@/components/walkthrough-icons";

/* ------------------------------------------------------------------ the payload */

/**
 * What `/onboarding/progress` answers.
 *
 * The counts for a module the organisation does not hold are **absent, not zero** — see the note
 * at the top of `apps/api/src/routes/onboarding-progress.ts`. `undefined` is therefore load-bearing
 * here: a step whose count is missing is a step that does not belong to this account at all, which
 * is a different thing from one that has not been done.
 */
export interface OnboardingProgress {
  modules: ModuleCode[];
  counts: {
    clients: number;
    properties?: number;
    units?: number;
    tenancies?: number;
    invoices?: number;
    estates?: number;
    plots?: number;
    purchases?: number;
  };
  agency: {
    address: boolean;
    collectionAccounts: number;
  };
}

export interface WalkthroughStep {
  key: string;
  title: string;
  /** What the step is and why it comes where it does. One or two sentences, formal. */
  detail: string;
  /** A worked example, behind the marker beside the title, so the prose stays short. */
  example: string;
  to: string;
  action: string;
  icon: ComponentType<{ size?: number }>;
  need?: Permission;
  done: boolean;
}

export interface WalkthroughSection {
  key: string;
  title: string;
  blurb: string;
  steps: WalkthroughStep[];
}

/* ------------------------------------------------------------------- the route */

/**
 * Every step, in the order they unblock one another.
 *
 * The addresses are the application's real routes and nothing else. A walkthrough that invents a
 * screen is worse than no walkthrough: it sends a brand-new owner to a "page not found" on their
 * first five minutes in the product. Each one below appears verbatim in `App.tsx`.
 *
 * The order is the dependency order, not a preference. A tenancy cannot be granted over a unit
 * that does not exist, and an invoice is raised against a tenancy — so completing them out of
 * sequence is possible but pointless, and the sequence is what makes the panel readable as a route
 * rather than as a list of everything.
 */
function lettingSteps(progress: OnboardingProgress, vocab: Vocabulary): WalkthroughStep[] {
  const counts = progress.counts;
  return [
    {
      key: "property",
      title: `Record a ${vocab.property}`,
      detail: `The building or site itself: its name, its address, and the ${vocab.landlord} it is held for. Everything else in the register hangs from it.`,
      example: `A block of six flats named Palm Court, at 17 Chapel Row, held for a single ${vocab.landlord}.`,
      to: "/app/properties/new",
      action: `Add a ${vocab.property}`,
      icon: IconBuilding,
      need: "property.edit",
      done: (counts.properties ?? 0) > 0,
    },
    {
      key: "units",
      title: `Add the ${vocab.units} inside it`,
      detail: `Each lettable space within the ${vocab.property}. A ${vocab.lease} is granted over a ${vocab.unit}, so the register cannot describe an occupancy until one exists.`,
      example: `Flat 1 through Flat 6 created in a single operation from the ${vocab.property}'s own screen, rather than six forms.`,
      to: "/app/units/new",
      action: `Add a ${vocab.unit}`,
      icon: IconDoor,
      need: "property.edit",
      done: (counts.units ?? 0) > 0,
    },
    {
      key: "client",
      title: "Add a client",
      detail: `Everyone the agency deals with — ${vocab.tenants}, ${vocab.landlords} and guarantors — in one contact list. A person may hold more than one of those roles at once.`,
      example: "A tenant recorded with two telephone numbers, the second of them marked for messaging.",
      to: "/app/clients/new",
      action: "Add a client",
      icon: IconUsers,
      need: "party.edit",
      done: counts.clients > 0,
    },
    {
      key: "tenancy",
      title: `Create a ${vocab.lease}`,
      detail: `The agreement joining a client to a ${vocab.unit}: its term, its ${vocab.rent}, its cycle and its charges. Creating it produces the schedule of what will be billed and when.`,
      example: `A twelve-month term on an annual cycle — one demand for the year, due once, in advance, rather than twelve monthly ones.`,
      to: "/app/tenancies/new",
      action: `Create a ${vocab.lease}`,
      icon: IconContract,
      need: "lease.edit",
      done: (counts.tenancies ?? 0) > 0,
    },
    {
      key: "invoice",
      title: `Raise the first ${vocab.invoice}`,
      detail: `The demand for what is owed. Once raised it is counted in the overdue and due-soon figures on your dashboard, and the reminder ladder can chase it.`,
      example: `The first year's ${vocab.rent}, issued against the ${vocab.lease}, falling due on the commencement date.`,
      to: "/app/invoices/new",
      action: `Raise an ${vocab.invoice}`,
      icon: IconInvoice,
      need: "invoice.edit",
      done: (counts.invoices ?? 0) > 0,
    },
  ];
}

function developingSteps(progress: OnboardingProgress): WalkthroughStep[] {
  const counts = progress.counts;
  return [
    /* The keys are the record's own name and are deliberately not renamed with the words.
       They are what `data-step` and the progress counts are keyed on — and `property` is already
       the letting step's key, so an account holding both modules would have two steps answering to
       one name. The words a person reads are the ones that changed. */
    {
      key: "estate",
      title: "Register the property being sold",
      detail:
        "The scheme being sold: where it is, what it consists of, and whether it is sold as land, as homes, as commercial space or as a mixture.",
      example: "A scheme of forty parcels, sold as land, with its survey plan and its status set to selling.",
      to: "/app/development/properties/new",
      action: "Add a property",
      icon: IconEstate,
      need: "property.edit",
      done: (counts.estates ?? 0) > 0,
    },
    {
      key: "plots",
      title: "Add the plots it is divided into",
      detail:
        "Each parcel offered for sale, with its size, its kind, and how many dwellings stand on it. They are added from the property's own screen, in a block rather than one at a time.",
      example:
        "Plots A1 to A40 created in one operation. One of them is a block of twelve flats — a single sale, and twelve homes.",
      to: "/app/development/properties",
      action: "Open the property",
      icon: IconPlot,
      need: "property.edit",
      done: (counts.plots ?? 0) > 0,
    },
    {
      key: "purchase",
      title: "Record a sale and its payment plan",
      detail:
        "The purchase joining a buyer to what they have bought, together with the instalments they will pay. The schedule is drawn as the terms are typed, and the instalments add back up to the agreed price exactly.",
      example: "A plot sold on a twenty-four month plan, paid monthly, with a deposit taken at the outset.",
      to: "/app/development/sales/new",
      action: "Record a sale",
      icon: IconKeys,
      need: "lease.edit",
      done: (counts.purchases ?? 0) > 0,
    },
  ];
}

function agencySteps(progress: OnboardingProgress): WalkthroughStep[] {
  return [
    {
      key: "details",
      title: "Complete the agency's own details",
      detail:
        "Your address and contact details are printed on every invoice, receipt and statement the agency issues. Until they are entered, those documents go out incomplete.",
      example: "The registered address, a telephone number, and the address correspondence should be returned to.",
      to: "/app/settings",
      action: "Open settings",
      icon: IconSettings,
      need: "org.settings",
      done: progress.agency.address,
    },
    {
      key: "bank",
      title: "Add the account clients pay into",
      detail:
        "A collection account is what a pay link and a printed invoice publish. An account marked for payouts is where the agency itself is paid, and is never shown to a payer.",
      example: "One collection account, set as the default, appearing on every pay link the agency sends.",
      to: "/app/bank-accounts",
      action: "Add an account",
      icon: IconBank,
      need: "org.settings",
      done: progress.agency.collectionAccounts > 0,
    },
  ];
}

/**
 * The whole route, narrowed to this account and this person.
 *
 * Exported so the verification suite and any future screen can reason about it without rendering
 * it, and pure so that it can be. Both narrowings happen here: the modules it is asked for, and
 * the permissions the signed-in member has.
 *
 * **`modules` is what the caller wants a route for, not what the account holds.** `useWalkthrough`
 * passes the single module the reader is *in*, and that is the whole of the fix for a crossing the
 * operator reported: an account holding both was given a Letting section and a Development section
 * on one page, so somebody standing in the development rail was offered five buttons — record a
 * property, add units, create a tenancy, raise an invoice — every one of which is a letting-owned
 * address. Pressing any of them moved the whole navigation into the other business. The letting
 * route has not gone anywhere; it is read from the letting rail, which is where it belongs.
 */
export function buildRoute(
  progress: OnboardingProgress,
  modules: ModuleCode[],
  vocab: Vocabulary,
  can: (permission: Permission) => boolean,
): WalkthroughSection[] {
  const sections: WalkthroughSection[] = [];

  for (const module of modules) {
    const info = MODULE_INFO[module];
    const steps = module === "letting" ? lettingSteps(progress, vocab) : developingSteps(progress);
    sections.push({ key: module, title: info.label, blurb: info.blurb, steps });
  }

  sections.push({
    key: "agency",
    title: "Your agency",
    blurb: "The details printed on the documents you issue, and the account money is collected into.",
    steps: agencySteps(progress),
  });

  /* A step this member may not perform is removed rather than disabled, and a section left with
     nothing in it goes with it — an empty heading is a worse answer than no heading. */
  return sections
    .map((section) => ({ ...section, steps: section.steps.filter((step) => !step.need || can(step.need)) }))
    .filter((section) => section.steps.length > 0);
}

/* ------------------------------------------------------- what the product is, in its own words */

const WHAT_IT_DOES: Record<ModuleCode, string> = {
  letting:
    "Your portfolio, your tenancies and the money against them are held in one register: what is let, what is vacant, what is owed, what falls due next, and who has to be chased for it. The promise the product is built to keep is that no expiry and no arrear is ever discovered after the fact.",
  developing:
    "Your properties, the plots within them and every sale against them are held in one register: what is available, what is reserved, what each buyer has paid, and which instalment falls due next. A payment plan is drawn as its terms are agreed, and every instalment is accounted for against the price.",
};

/** The sentences that describe the product to this particular account. */
export function whatItDoes(modules: ModuleCode[]): string[] {
  return modules.map((module) => WHAT_IT_DOES[module]);
}

/* ------------------------------------------------------------------- the dismissal */

const KEY_PREFIX = "rentorng.getting-started.dismissed.";

/*
  A tiny store rather than `useState`.

  The guide is read in two places at once — the panel on this screen and the summary the dashboard
  carries — and dismissing it in one has to settle the other immediately, in the same paint. A
  `storage` event is listened for as well so a second tab agrees rather than showing a panel that
  has already been dismissed.
*/
const listeners = new Set<() => void>();
const announce = () => listeners.forEach((listener) => listener());

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

/** Reading `localStorage` can throw outright where site data is blocked, so it never goes unguarded. */
function readDismissed(orgId: string | null): boolean {
  if (!orgId) return false;
  try {
    return window.localStorage.getItem(KEY_PREFIX + orgId) === "1";
  } catch {
    return false;
  }
}

export function setWalkthroughDismissed(orgId: string | null, dismissed: boolean): void {
  if (!orgId) return;
  try {
    if (dismissed) window.localStorage.setItem(KEY_PREFIX + orgId, "1");
    else window.localStorage.removeItem(KEY_PREFIX + orgId);
  } catch {
    /* A browser refusing to store it is a browser that will show the guide again. That is the
       right way round: the failure shows something rather than hiding it. */
  }
  announce();
}

/**
 * Whether this organisation has put the guide away, and the two ways to change that.
 *
 * Exported on its own so a Settings screen can offer "show it again" without pulling in the panel.
 */
export function useWalkthroughDismissal() {
  const { org } = useSession();
  const orgId = org?.id ?? null;
  const dismissed = useSyncExternalStore(
    subscribe,
    () => readDismissed(orgId),
    () => false,
  );
  return {
    dismissed,
    dismiss: useCallback(() => setWalkthroughDismissed(orgId, true), [orgId]),
    restore: useCallback(() => setWalkthroughDismissed(orgId, false), [orgId]),
  };
}

/* ------------------------------------------------------------------- the hook */

export interface WalkthroughState {
  loading: boolean;
  error: unknown;
  sections: WalkthroughSection[];
  steps: WalkthroughStep[];
  /** The first step still outstanding — what this person should do next, or nothing if finished. */
  next: WalkthroughStep | null;
  done: number;
  total: number;
  complete: boolean;
  dismissed: boolean;
  dismiss: () => void;
  restore: () => void;
}

/**
 * The walkthrough, resolved against this account's real data.
 *
 * `staleTime` is zero on purpose. The whole point is that returning from having added a property
 * shows the property step done; a cached answer from four minutes ago shows it outstanding and
 * teaches the reader that the panel is not watching.
 */
export function useWalkthrough(): WalkthroughState {
  const { org, modules, vocab, can } = useSession();
  /*
    The route is the route for the module the reader is **in**, not for every module the account
    holds. `/app/getting-started` belongs to neither module, so without this it drew both routes
    at once — and half its buttons then pointed at the other business. See `buildRoute`.
  */
  const active = useActiveModule(modules);
  const { dismissed, dismiss, restore } = useWalkthroughDismissal();

  const { data, isLoading, error } = useQuery({
    queryKey: ["onboarding-progress", org?.id],
    queryFn: () => api.get<OnboardingProgress>("/onboarding/progress"),
    enabled: Boolean(org?.id),
    staleTime: 0,
    retry: false,
  });

  const sections = useMemo(
    () => (data ? buildRoute(data, [active], vocab, can) : []),
    // `can` is rebuilt with the session; the route only changes when what it is built from does.
    [data, active, vocab, can],
  );

  const steps = useMemo(() => sections.flatMap((section) => section.steps), [sections]);
  const doneCount = steps.filter((step) => step.done).length;

  return {
    loading: isLoading,
    error,
    sections,
    steps,
    next: steps.find((step) => !step.done) ?? null,
    done: doneCount,
    total: steps.length,
    complete: steps.length > 0 && doneCount === steps.length,
    dismissed,
    dismiss,
    restore,
  };
}

/* ------------------------------------------------------------------- the drawing */

/**
 * One step.
 *
 * A completed step **states that it is complete and stops asking**. It keeps its place — removing
 * it would make the route shorter every time somebody looked, and the sequence is half of what the
 * panel is saying — but it loses its heading weight, its explanation and its button. What is left
 * is a settled line with a tick, which is the visual difference between a record of progress and a
 * list of demands.
 */
function Step({ step, next }: { step: WalkthroughStep; next: boolean }) {
  const Mark = step.icon;
  /* Two of the agency steps — Settings and Bank accounts — are screens both modules share, so the
     button keeps the module it was pressed from. The module's own steps are module-owned addresses
     and `withModule` leaves them exactly as written. */
  const { modules } = useSession();
  const link = useModuleLink(modules);

  if (step.done) {
    return (
      <li className="flex items-center gap-3 py-3" data-step={step.key} data-done="yes">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-100 text-brand-700">
          <IconCheck size={16} />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-ink-500">{step.title}</span>
        <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-brand-700">Complete</span>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-start gap-3 py-3.5 sm:flex-nowrap" data-step={step.key} data-done="no">
      <span
        className={`grid size-8 shrink-0 place-items-center rounded-full ${
          next ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-500"
        }`}
      >
        <Mark size={17} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-ink-900">{step.title}</p>
          <InfoTip label={`An example of ${step.title.toLowerCase()}`}>
            <span className="block font-medium text-ink-800">For example</span>
            <span className="mt-1 block">{step.example}</span>
          </InfoTip>
          {next ? (
            <span className="pill border-brand-200 bg-brand-50 text-brand-700">Next</span>
          ) : null}
        </div>
        <p className="mt-1 text-sm leading-relaxed text-ink-500">{step.detail}</p>
      </div>

      <div className="shrink-0 pt-0.5">
        <LinkButton to={link(step.to)} size="sm" variant={next ? "primary" : "secondary"} need={step.need}>
          {step.action}
        </LinkButton>
      </div>
    </li>
  );
}

/**
 * `next` is passed in rather than worked out here, and that is the whole reason this takes a prop.
 *
 * Each section computing its own first-outstanding step put a "Next" badge on two rows at once —
 * one in Letting and one in Your agency — which turns the single instruction the panel exists to
 * give into a choice. There is one next step in a route, or the route is a menu.
 */
function Section({ section, next }: { section: WalkthroughSection; next: WalkthroughStep | null }) {
  return (
    <div className="border-t border-ink-100 pt-5 first:border-t-0 first:pt-0" data-section={section.key}>
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-500">{section.title}</h3>
        <span className="text-sm text-ink-400">
          {section.steps.filter((step) => step.done).length} of {section.steps.length} complete
        </span>
      </div>
      <p className="mt-1 text-sm text-ink-500">{section.blurb}</p>
      <ul className="mt-2 divide-y divide-ink-100">
        {section.steps.map((step) => (
          <Step key={step.key} step={step} next={step === next} />
        ))}
      </ul>
    </div>
  );
}

/**
 * The full panel, as it appears on `/app/getting-started`.
 *
 * It is shown whether or not the guide has been dismissed. Dismissing governs where the summary
 * *volunteers* itself; the screen somebody navigated to deliberately should not answer them with
 * an empty page saying they once pressed a button.
 */
export function WalkthroughPanel() {
  const state = useWalkthrough();

  if (state.loading) return <Spinner label="Reading your records…" />;

  if (state.error) {
    return (
      <Card>
        <p className="text-sm text-ink-600">
          Your progress could not be read just now, so the steps below cannot be marked. Everything
          in the guide is still reachable from the navigation.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <div data-walkthrough="panel">
        <div className="mb-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-semibold text-ink-900">
              {state.complete ? "Every step is complete." : "Your progress"}
            </p>
            <p className="tabular text-sm text-ink-500" data-walkthrough-progress>
              {state.done} of {state.total} complete
            </p>
          </div>
          <div className="mt-2">
            <MeterBar value={state.done} max={state.total} tone="brand" />
          </div>
          {state.complete ? (
            <p className="mt-3 text-sm text-ink-500">
              The register is set up. This guide stays here for reference, and no longer appears
              anywhere else.
            </p>
          ) : null}
        </div>

        <div className="space-y-5">
          {state.sections.map((section) => (
            <Section key={section.key} section={section} next={state.next} />
          ))}
        </div>
      </div>
    </Card>
  );
}

/**
 * Where the guide is offered, and how to stop it being offered.
 *
 * Deliberately explicit about the consequence in both directions. "Dismiss" with no statement of
 * what it dismisses is a button somebody presses once and then cannot find their way back from,
 * which is the complaint that produced the Settings entry.
 */
export function WalkthroughVisibility() {
  const { dismissed, dismiss, restore } = useWalkthroughDismissal();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3" data-walkthrough-visibility={dismissed ? "dismissed" : "showing"}>
      <p className="text-sm text-ink-500">
        {dismissed
          ? "This guide has been dismissed. It no longer appears on your dashboard, and remains available here."
          : "A summary of this guide appears on your dashboard until it is dismissed."}
      </p>
      {dismissed ? (
        <Button variant="secondary" size="sm" onClick={restore}>
          Show the guide again
        </Button>
      ) : (
        <Button variant="secondary" size="sm" onClick={dismiss}>
          Dismiss the guide
        </Button>
      )}
    </div>
  );
}

/**
 * The summary, for a screen that is about something else.
 *
 * Draws nothing at all when there is nothing to say — dismissed, finished, still loading, or a
 * member with no step they may perform. A panel that renders an empty shell while it waits is a
 * layout shift on the busiest screen in the product.
 */
export function GettingStartedSummary() {
  const state = useWalkthrough();
  const { modules } = useSession();
  const link = useModuleLink(modules);

  if (state.loading || state.error) return null;
  if (state.dismissed || state.complete || state.total === 0 || !state.next) return null;

  return (
    <div
      className="mb-5 rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50 p-4"
      data-walkthrough="summary"
    >
      <div className="flex flex-wrap items-start gap-4">
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-600 text-white">
          <IconFlag size={18} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-brand-900">
            Getting started — {state.done} of {state.total} steps complete
          </p>
          <p className="mt-0.5 text-sm text-brand-800">
            Next: {state.next.title}. {state.next.detail}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <LinkButton to={link(state.next.to)} size="sm" need={state.next.need}>
              {state.next.action}
            </LinkButton>
            <Link
              to={link("/app/getting-started")}
              className="btn btn-secondary btn-sm"
            >
              See all steps
              <IconArrowRight size={15} />
            </Link>
          </div>
        </div>

        <Button variant="ghost" size="sm" onClick={state.dismiss} aria-label="Dismiss the getting started guide">
          Dismiss
        </Button>
      </div>
    </div>
  );
}
